import React from "react";
import { Box, Button, Card, CardContent, Paper, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";

const highlights = [
  {
    icon: <EmojiEventsRoundedIcon />,
    title: "National rankings",
    text: "Explore the athletes setting the standard across the country, with national and state context for every profile.",
    action: "Explore rankings",
    to: "/rankings/men",
  },
  {
    icon: <TimerRoundedIcon />,
    title: "Performance context",
    text: "Use Event Scores and equivalent performances to understand how marks compare across different events.",
    action: "Compare performances",
    to: "/scoring",
  },
  {
    icon: <TrackChangesRoundedIcon />,
    title: "Built for track & field",
    text: "Sprint, hurdle, distance, and field-event athletes are all considered through track-specific analytics.",
    action: "How it works",
    to: "/about",
  },
];

const Home = () => (
  <Box sx={{ maxWidth: 1320, mx: "auto", py: { xs: 1, md: 3 } }}>
    <Box sx={{ overflow: "hidden", display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.08fr 0.92fr" }, minHeight: { xs: 600, md: 510 }, borderRadius: 5, bgcolor: "#0b2035", boxShadow: "0 16px 36px rgba(8,31,53,0.18)" }}>
      <Box sx={{ position: "relative", zIndex: 1, px: { xs: 3, md: 7 }, py: { xs: 5, md: 7 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Box sx={{ width: "100%", height: { xs: 112, md: 145 }, overflow: "hidden", bgcolor: "#ffffff", borderRadius: 1.5, mb: 2.25 }}>
          <Box component="img" src="/images/nytrax-full-logo.png" alt="NYTRAX" sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} />
        </Box>
        <Typography sx={{ color: "#ef4136", fontWeight: 950, fontSize: 12, letterSpacing: 1.8, textTransform: "uppercase" }}>NYTRAX - National Youth Track Athlete Index</Typography>
        <Typography component="h1" sx={{ color: "#ffffff", fontFamily: "'Trebuchet MS', 'Arial Black', sans-serif", fontSize: { xs: 43, md: 62 }, fontWeight: 950, letterSpacing: -1.6, lineHeight: 1, mt: 1.4, maxWidth: 650 }}>
          Every mark has a place on the track.
        </Typography>
        <Typography sx={{ color: "#c8d8e7", fontSize: { xs: 17, md: 19 }, lineHeight: 1.65, maxWidth: 580, mt: 2.3 }}>
          Follow the athletes, performances, and rankings that define high school track and field across the country.
        </Typography>
        <Typography sx={{ color: "#90aec5", fontSize: 14, lineHeight: 1.55, mt: 1.5, maxWidth: 550 }}>
          One of the first youth running analytics platforms built with AI-assisted analysis, machine-learning-informed design, and track-specific performance analytics.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mt: 3.5 }}>
          <Button component={Link} to="/rankings/men" variant="contained" size="large" sx={{ bgcolor: "#d52b24", color: "#ffffff", fontWeight: 950, px: 3, "&:hover": { bgcolor: "#b91f1a" } }}>View men's rankings</Button>
          <Button component={Link} to="/rankings/women" variant="contained" size="large" sx={{ bgcolor: "#d52b24", color: "#ffffff", fontWeight: 950, px: 3, "&:hover": { bgcolor: "#b91f1a" } }}>View women's rankings</Button>
        </Stack>
      </Box>

      <Box sx={{ position: "relative", minHeight: { xs: 330, md: "auto" }, overflow: "hidden", background: "radial-gradient(circle at 20% 18%, #245780 0%, #122941 33%, #0b1728 72%)" }}>
        <Box sx={{ position: "absolute", width: 330, height: 330, borderRadius: "50%", right: -155, top: -115, bgcolor: "rgba(213,43,36,0.24)", filter: "blur(3px)" }} />
        <Box sx={{ position: "absolute", width: 290, height: 290, borderRadius: "50%", left: -160, bottom: -145, bgcolor: "rgba(40,78,121,0.25)", filter: "blur(2px)" }} />
        <Box component="img" src="/images/athlete-silhouettes.png" alt="Sprinter, runner, and high jumper silhouettes" sx={{ position: "absolute", zIndex: 2, width: { xs: "106%", md: "118%" }, maxWidth: "none", height: "auto", right: { xs: "-2%", md: 0 }, top: "50%", transform: "translateY(-50%)", filter: "drop-shadow(0 16px 18px rgba(0,0,0,0.38))" }} />
      </Box>
    </Box>

    <Paper elevation={0} sx={{ mt: 3, overflow: "hidden", border: "1px solid #dce4ee", borderRadius: 3, bgcolor: "#ffffff" }}>
      <Box sx={{ px: { xs: 2.5, md: 3 }, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #dce4ee" }}>
        <Typography sx={{ color: "#c92520", fontSize: 11, letterSpacing: 1.2, fontWeight: 950, textTransform: "uppercase" }}>NYTRAX at a glance</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" } }}>
        <Box sx={{ p: { xs: 2.25, md: 2.75 }, borderBottom: { xs: "1px solid #e8edf3", md: "none" }, borderRight: { md: "1px solid #e8edf3" } }}>
          <Typography sx={{ color: "#c92520", fontSize: 12, letterSpacing: 1, fontWeight: 950 }}>01</Typography>
          <Typography sx={{ mt: 0.35, color: "#071b33", fontSize: 18, fontWeight: 950 }}>TRAX-Score</Typography>
          <Typography sx={{ mt: 0.45, color: "#607086", fontSize: 14, lineHeight: 1.6 }}>The overall athlete rating, built from performance level, consistency, versatility, and Big Meet context.</Typography>
        </Box>
        <Box sx={{ p: { xs: 2.25, md: 2.75 }, borderBottom: { xs: "1px solid #e8edf3", md: "none" }, borderRight: { md: "1px solid #e8edf3" } }}>
          <Typography sx={{ color: "#c92520", fontSize: 12, letterSpacing: 1, fontWeight: 950 }}>02</Typography>
          <Typography sx={{ mt: 0.35, color: "#071b33", fontSize: 18, fontWeight: 950 }}>Event Score</Typography>
          <Typography sx={{ mt: 0.45, color: "#607086", fontSize: 14, lineHeight: 1.6 }}>A common-scale value for one mark, making it easier to compare equivalent performances across events.</Typography>
        </Box>
        <Box sx={{ p: { xs: 2.25, md: 2.75 } }}>
          <Typography sx={{ color: "#c92520", fontSize: 12, letterSpacing: 1, fontWeight: 950 }}>03</Typography>
          <Typography sx={{ mt: 0.35, color: "#071b33", fontSize: 18, fontWeight: 950 }}>Rankings</Typography>
          <Typography sx={{ mt: 0.45, color: "#607086", fontSize: 14, lineHeight: 1.6 }}>National and state context helps athletes, families, and coaches understand where each profile stands.</Typography>
        </Box>
      </Box>
    </Paper>

    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2, mt: 3 }}>
      {highlights.map((highlight) => (
        <Card key={highlight.title} elevation={0} sx={{ border: "1px solid #dfe3e8", borderRadius: 3, bgcolor: "#ffffff" }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "grid", placeItems: "center", width: 40, height: 40, borderRadius: 2, bgcolor: "#fbe9e7", color: "#c92520", mb: 1.5 }}>{highlight.icon}</Box>
            <Typography sx={{ color: "#071b33", fontWeight: 950, fontSize: 18 }}>{highlight.title}</Typography>
            <Typography sx={{ color: "#607791", fontSize: 14, lineHeight: 1.55, mt: 0.6 }}>{highlight.text}</Typography>
            <Button component={Link} to={highlight.to} size="small" sx={{ mt: 1.25, px: 0, minWidth: 0, color: "#c92520", fontWeight: 900, textTransform: "none", "&:hover": { bgcolor: "transparent", color: "#a91b17" } }}>
              {highlight.action}
            </Button>
          </CardContent>
        </Card>
      ))}
    </Box>

    <Box sx={{ mt: 4, py: { xs: 1, md: 2 }, display: "grid", gridTemplateColumns: { xs: "1fr", md: "0.8fr 1.2fr" }, gap: { xs: 1.5, md: 5 }, alignItems: "start" }}>
      <Box>
        <Typography sx={{ color: "#c92520", fontSize: 11, letterSpacing: 1.3, fontWeight: 950, textTransform: "uppercase" }}>For the track community</Typography>
        <Typography sx={{ mt: 0.45, color: "#071b33", fontSize: { xs: 26, md: 32 }, fontWeight: 950, lineHeight: 1.15 }}>More than a list of times and marks.</Typography>
      </Box>
      <Typography sx={{ color: "#607086", fontSize: { xs: 15, md: 16 }, lineHeight: 1.75 }}>
        NYTRAX brings athlete results, event context, and broader performance signals together in one place. Use the rankings to follow the athletes shaping the sport, visit profiles for a closer look at their body of work, and use Equivalent Performances to compare events on a common scale.
      </Typography>
    </Box>

    <Box sx={{ mt: 3, display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", md: "row" }, gap: 2, borderRadius: 3, p: { xs: 2.5, md: 3 }, bgcolor: "#071b33", color: "#ffffff", borderLeft: "6px solid #c92520" }}>
      <Box>
        <Typography sx={{ fontWeight: 950, fontSize: 21 }}>How do performances stack up?</Typography>
        <Typography sx={{ color: "#c9d2dd", mt: 0.4 }}>Compare marks across events and calculate an Event Score.</Typography>
      </Box>
      <Button component={Link} to="/scoring" variant="contained" sx={{ bgcolor: "#c92520", color: "#ffffff", fontWeight: 900, whiteSpace: "nowrap", "&:hover": { bgcolor: "#a91b17" } }}>Compare performances</Button>
    </Box>
  </Box>
);

export default Home;
